import React, { useState } from 'react';
import {
  Typography, Form, Input, Button, message, Segmented, Steps, Alert,
} from 'antd';
import {
  Settings as SettingsIcon, ArrowLeft, Save, Phone, User, Sun, Moon, Monitor,
  MessageCircle, CheckCircle, Edit3,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SimpleDashboardLayout from '../components/layout/SimpleDashboardLayout';
import OTPInput from '../components/OTPInput';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import type { ThemeMode } from '../context/ThemeContext';
import { api } from '../services/api';

const { Title, Text } = Typography;

// ── Shared card style ────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderColor: 'var(--color-border)',
};

// ── Theme options ────────────────────────────────────────────────────────────
const themeOptions = [
  { label: <span className="flex items-center gap-1.5"><Sun className="w-3.5 h-3.5" /> Light</span>, value: 'light' },
  { label: <span className="flex items-center gap-1.5"><Moon className="w-3.5 h-3.5" /> Dark</span>, value: 'dark' },
  { label: <span className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5" /> System</span>, value: 'system' },
];

type PhoneStep = 'idle' | 'new_phone' | 'otp' | 'done';

const SimpleSettings: React.FC = () => {
  const { user, setAuth, accessToken, refreshToken: storedRefresh } = useAuthStore();
  const { t } = useLanguage();
  const { mode, setMode } = useTheme();
  const [nameForm] = Form.useForm();
  const [nameLoading, setNameLoading] = useState(false);

  // Phone change state machine
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('idle');
  const [newPhone, setNewPhone] = useState('');
  const [rawPhone, setRawPhone] = useState(''); // without +91
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Populate name form
  React.useEffect(() => {
    if (user) {
      nameForm.setFieldsValue({ name: user.name });
    }
  }, [user, nameForm]);

  // ── Name-only save ──────────────────────────────────────────────────────────
  const handleNameSave = async (values: any) => {
    setNameLoading(true);
    try {
      await api.patch('/profile/update-name', { name: values.name });
      if (user) {
        setAuth(
          { ...user, name: values.name },
          accessToken || '',
          storedRefresh || ''
        );
      }
      message.success('Name updated!');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to update name');
    } finally {
      setNameLoading(false);
    }
  };

  // ── Phone change: step 1 — request OTP ─────────────────────────────────────
  const handleRequestOTP = async () => {
    if (!rawPhone || rawPhone.length !== 10) {
      message.warning('Enter a valid 10-digit number');
      return;
    }
    const full = `91${rawPhone}`;
    setSendingOTP(true);
    try {
      await api.post('/profile/request-phone-change', { newPhone: full });
      setNewPhone(full);
      setPhoneStep('otp');
      message.success('OTP sent to your new WhatsApp number!');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  // ── Phone change: step 2 — verify OTP ──────────────────────────────────────
  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setOtpError(true);
      return;
    }
    setVerifying(true);
    setOtpError(false);
    try {
      const { data } = await api.patch('/profile/update-profile', {
        newPhone,
        otp,
        name: user?.name,
      });
      if (user) {
        setAuth(
          { ...user, phone: data.data.phone },
          accessToken || '',
          storedRefresh || ''
        );
      }
      setPhoneStep('done');
      message.success('Phone number updated!');
    } catch (err: any) {
      setOtpError(true);
      message.error(err?.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifying(false);
    }
  };

  const resetPhoneFlow = () => {
    setPhoneStep('idle');
    setNewPhone('');
    setRawPhone('');
    setOtp('');
    setOtpError(false);
  };

  const formatPhone = (v: string) => v.replace(/\D/g, '').slice(0, 10);

  return (
    <SimpleDashboardLayout>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {/* Page Header */}
        <div className="flex items-center mb-6">
          <Link to="/dashboard" className="mr-4">
            <Button type="text" icon={<ArrowLeft className="w-4 h-4" />} aria-label={t('back')} />
          </Link>
          <div>
            <Title level={4} className="mb-0 flex items-center" style={{ color: 'var(--color-text)' }}>
              <SettingsIcon className="w-6 h-6 mr-2 text-gray-500 dark:text-gray-400" />
              {t('settings')}
            </Title>
            <Text type="secondary">{t('update_profile')}</Text>
          </div>
        </div>

        {/* ── Appearance ──────────────────────────────────────────────────── */}
        <div className="p-4 rounded-lg shadow-sm border mb-4" style={cardStyle}>
          <Title level={5} className="mb-1" style={{ color: 'var(--color-text)' }}>Appearance</Title>
          <Text type="secondary" className="block mb-3">Choose how DhartiNetra looks on this device.</Text>
          <Segmented
            options={themeOptions}
            value={mode}
            onChange={(val: string | number) => setMode(val as ThemeMode)}
            size="large"
            block
          />
        </div>

        {/* ── Name ────────────────────────────────────────────────────────── */}
        <div className="p-4 rounded-lg shadow-sm border mb-4" style={cardStyle}>
          <Title level={5} className="mb-3" style={{ color: 'var(--color-text)' }}>{t('your_name')}</Title>
          <Form form={nameForm} layout="inline" onFinish={handleNameSave} style={{ width: '100%' }}>
            <Form.Item name="name" rules={[{ required: true, message: 'Name is required' }]} style={{ flex: 1 }}>
              <Input
                placeholder={t('name_placeholder')}
                size="large"
                prefix={<User className="w-4 h-4 text-gray-400 mr-1" />}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={nameLoading}
                icon={<Save className="w-4 h-4" />}
                size="large"
              >
                {t('save')}
              </Button>
            </Form.Item>
          </Form>
        </div>

        {/* ── WhatsApp Number (OTP-gated) ──────────────────────────────────── */}
        <div className="p-4 rounded-lg shadow-sm border mb-4" style={cardStyle}>
          <Title level={5} className="mb-1" style={{ color: 'var(--color-text)' }}>
            {t('whatsapp_number')}
          </Title>

          {/* Current number display */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <Text style={{ color: 'var(--color-text-secondary)' }}>
                Current: <strong style={{ color: 'var(--color-text)' }}>+{user?.phone || '—'}</strong>
              </Text>
            </div>
            {phoneStep === 'idle' && (
              <Button
                size="small"
                icon={<Edit3 className="w-3.5 h-3.5" />}
                onClick={() => setPhoneStep('new_phone')}
              >
                Change
              </Button>
            )}
            {(phoneStep !== 'idle' && phoneStep !== 'done') && (
              <Button size="small" danger onClick={resetPhoneFlow}>Cancel</Button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {/* Step: enter new number */}
            {phoneStep === 'new_phone' && (
              <motion.div
                key="new_phone"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Alert
                  type="info"
                  showIcon
                  message="OTP Verification Required"
                  description="Enter your new WhatsApp number. We'll send an OTP to confirm it."
                  className="mb-3"
                />
                <div className="flex gap-2">
                  <Input
                    addonBefore="+91"
                    placeholder="10-digit number"
                    size="large"
                    value={rawPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRawPhone(formatPhone(e.target.value))}
                    maxLength={10}
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="primary"
                    size="large"
                    icon={<MessageCircle className="w-4 h-4" />}
                    loading={sendingOTP}
                    onClick={handleRequestOTP}
                    disabled={rawPhone.length !== 10}
                  >
                    Send OTP
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step: enter OTP */}
            {phoneStep === 'otp' && (
              <motion.div
                key="otp"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                <Alert
                  type="success"
                  showIcon
                  message={`OTP sent to +${newPhone}`}
                  description="Check your WhatsApp and enter the 6-digit OTP below."
                />
                <Steps
                  size="small"
                  current={1}
                  items={[
                    { title: 'New Number' },
                    { title: 'Verify OTP' },
                    { title: 'Done' },
                  ]}
                  className="mb-4"
                />
                <OTPInput value={otp} onChange={setOtp} error={otpError} />
                {otpError && (
                  <Text className="block text-center" style={{ color: 'var(--color-danger)' }}>
                    Invalid or expired OTP. Please try again.
                  </Text>
                )}
                <div className="flex gap-2 justify-center">
                  <Button
                    type="primary"
                    size="large"
                    loading={verifying}
                    icon={<CheckCircle className="w-4 h-4" />}
                    onClick={handleVerifyOTP}
                    disabled={otp.length !== 6}
                  >
                    Verify & Update
                  </Button>
                  <Button
                    size="large"
                    onClick={() => setPhoneStep('new_phone')}
                  >
                    Resend
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step: done */}
            {phoneStep === 'done' && (
              <motion.div
                key="done"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Alert
                  type="success"
                  showIcon
                  message="Phone number updated successfully!"
                  action={<Button size="small" onClick={resetPhoneFlow}>Dismiss</Button>}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Note */}
          <div
            className="mt-3 px-3 py-2 rounded-md border-l-4 border-amber-400 dark:border-amber-500"
            style={{ background: 'var(--color-surface)' }}
          >
            <Text style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              <strong style={{ color: 'var(--color-text)' }}>{t('note')}:</strong>{' '}
              {t('settings_note')}
            </Text>
          </div>
        </div>
      </motion.div>
    </SimpleDashboardLayout>
  );
};

export default SimpleSettings;
